import { useEffect, useRef, useState } from "react";
import { frameDiffRatio, detectMotion } from "@/lib/kiosk";

interface UseCameraMotionOptions {
  enabled: boolean;
  onMotion?: () => void;
  /** Interval between frame samples in milliseconds. Default 800. */
  intervalMs?: number;
}

interface UseCameraMotionReturn {
  /** True when getUserMedia is available in this browser. */
  supported: boolean;
  /** True when the camera stream is currently running. */
  active: boolean;
  /** Set when permission is denied or the API is unavailable; null otherwise. */
  error: string | null;
}

/**
 * Samples frames from the user's camera at `intervalMs` intervals and calls
 * `onMotion` whenever the frame-diff ratio between consecutive frames exceeds
 * the `detectMotion` threshold.
 *
 * Lifecycle:
 * - When `enabled` becomes true: `getUserMedia({ video: true })` is requested.
 *   If the user denies permission or the API is absent, `error` is set and the
 *   hook no-ops (no throw, no crash).
 * - Frames are captured via a small offscreen canvas (64 × 48 px).
 * - When `enabled` becomes false or the component unmounts, the camera stream
 *   is stopped and the sampling interval is cleared.
 *
 * SSR-safe: all `navigator`/`window` access lives inside `useEffect`.
 */
export function useCameraMotion({
  enabled,
  onMotion,
  intervalMs = 800,
}: UseCameraMotionOptions): UseCameraMotionReturn {
  const supported =
    typeof navigator !== "undefined" && typeof navigator.mediaDevices?.getUserMedia === "function";

  const [active, setActive] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onMotionRef = useRef(onMotion);
  useEffect(() => {
    onMotionRef.current = onMotion;
  }, [onMotion]);

  const streamRef = useRef<MediaStream | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const prevFrameRef = useRef<Uint8ClampedArray | null>(null);

  function stopCamera() {
    if (intervalRef.current !== null) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    if (streamRef.current) {
      for (const track of streamRef.current.getTracks()) {
        track.stop();
      }
      streamRef.current = null;
    }
    prevFrameRef.current = null;
    setActive(false);
  }

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!supported) return;
    if (!enabled) {
      stopCamera();
      return;
    }

    let cancelled = false;

    async function startCamera() {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ video: true });
        if (cancelled) {
          for (const track of stream.getTracks()) track.stop();
          return;
        }

        streamRef.current = stream;
        setActive(true);
        setError(null);

        // Set up an offscreen canvas (64 × 48 — small enough to be fast).
        const W = 64;
        const H = 48;
        const canvas = document.createElement("canvas");
        canvas.width = W;
        canvas.height = H;
        const ctx = canvas.getContext("2d");

        const video = document.createElement("video");
        video.srcObject = stream;
        video.playsInline = true;
        video.muted = true;
        await video.play();

        if (cancelled) {
          for (const track of stream.getTracks()) track.stop();
          return;
        }

        intervalRef.current = setInterval(() => {
          if (!ctx) return;
          ctx.drawImage(video, 0, 0, W, H);
          const frame = ctx.getImageData(0, 0, W, H).data;

          if (prevFrameRef.current) {
            const ratio = frameDiffRatio(prevFrameRef.current, frame);
            if (detectMotion(ratio)) {
              onMotionRef.current?.();
            }
          }

          prevFrameRef.current = new Uint8ClampedArray(frame);
        }, intervalMs);
      } catch (err) {
        if (cancelled) return;
        const message = err instanceof Error ? err.message : "Camera unavailable";
        setError(message);
        setActive(false);
      }
    }

    void startCamera();

    return () => {
      cancelled = true;
      stopCamera();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, intervalMs, supported]);

  return { supported, active, error };
}
